import React, { useMemo, useState } from "react";
import { PageHeader, Content } from "@/components/PageChrome";
import { api, formatApiError } from "@/lib/api";
import {
  Building2, Pencil, Save, Trash2, Plus, EyeOff, X,
} from "lucide-react";
import { toast } from "sonner";
import { useCompanies } from "@/context/CompanyContext";

const inputCls =
  "w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm " +
  "focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none";

const EMPTY = {
  name: "", label: "", address: "", city: "", state: "", country: "India",
  postal_code: "", phone: "", email: "", website: "", gstin: "", pan: "",
  base_currency: "INR", bank_name: "", bank_account: "", bank_ifsc: "",
};

const EDITABLE = Object.keys(EMPTY);

function CompanyForm({ initial, onSaved, onCancel, submitLabel = "Save profile" }) {
  const [form, setForm] = useState(() => {
    const next = { ...EMPTY };
    EDITABLE.forEach((k) => {
      if (initial?.[k] != null) next[k] = initial[k];
    });
    return next;
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSaved(form);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Registered name</label>
          <input required className={inputCls} value={form.name} onChange={set("name")}
                 placeholder="Acme Private Limited" data-testid="company-form-name" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">
            Label <span className="text-gray-400">(optional)</span>
          </label>
          <input className={inputCls} value={form.label} onChange={set("label")}
                 placeholder="Bangalore branch" data-testid="company-form-label" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-gray-600 block mb-1.5">Address</label>
          <input className={inputCls} value={form.address} onChange={set("address")}
                 data-testid="company-form-address" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">City</label>
          <input className={inputCls} value={form.city} onChange={set("city")}
                 data-testid="company-form-city" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">
            State <span className="text-gray-400">(drives GST split)</span>
          </label>
          <input className={inputCls} value={form.state} onChange={set("state")}
                 placeholder="Karnataka" data-testid="company-form-state" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">PIN code</label>
          <input className={inputCls} value={form.postal_code} onChange={set("postal_code")}
                 data-testid="company-form-postal" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Country</label>
          <input className={inputCls} value={form.country} onChange={set("country")}
                 data-testid="company-form-country" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Phone</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")}
                 data-testid="company-form-phone" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Email</label>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")}
                 data-testid="company-form-email" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Website</label>
          <input className={inputCls} value={form.website} onChange={set("website")}
                 data-testid="company-form-website" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">GSTIN</label>
          <input className={inputCls} value={form.gstin} onChange={set("gstin")}
                 data-testid="company-form-gstin" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">PAN</label>
          <input className={inputCls} value={form.pan} onChange={set("pan")}
                 data-testid="company-form-pan" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Base currency</label>
          <select className={inputCls} value={form.base_currency}
                  onChange={set("base_currency")} data-testid="company-form-currency">
            <option value="INR">INR — Indian Rupee</option>
            <option value="USD">USD — US Dollar</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Bank name</label>
          <input className={inputCls} value={form.bank_name} onChange={set("bank_name")}
                 data-testid="company-form-bank-name" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">Account number</label>
          <input className={inputCls} value={form.bank_account} onChange={set("bank_account")}
                 data-testid="company-form-bank-account" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1.5">IFSC</label>
          <input className={inputCls} value={form.bank_ifsc} onChange={set("bank_ifsc")}
                 data-testid="company-form-bank-ifsc" />
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={busy}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="company-form-submit">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : submitLabel}
        </button>
        <button type="button" onClick={onCancel}
                className="px-4 h-10 border border-[var(--border)] rounded-md text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function Companies() {
  const { companies, activeCompany, limit, used, canAddCompany, refresh, switchCompany } =
    useCompanies();
  const [editing, setEditing] = useState(null); // company id | "new"
  const [busyId, setBusyId] = useState(null);

  const editingCompany = useMemo(
    () => (editing === "new" ? null : companies.find((c) => c.id === editing) || null),
    [editing, companies],
  );

  const createCompany = async (form) => {
    const { data } = await api.post("/companies", form);
    await refresh();
    // The first profile becomes active server-side; mirror that immediately.
    if (!activeCompany) {
      const created = data?.companies?.find((c) => c.is_default) || data?.companies?.[0];
      if (created) await switchCompany(created.id);
    }
    toast.success(`Company "${form.name}" added`);
    setEditing(null);
  };

  const saveCompany = async (form) => {
    await api.put(`/companies/${editing}`, form);
    await refresh();
    toast.success("Company profile updated");
    setEditing(null);
  };

  const removeCompany = async (c) => {
    const docs = c.doc_count || 0;
    // Clients are account-wide, so deleting a company never touches them.
    const warn = docs
      ? `\n\nThis will permanently delete ${docs} document(s) issued by this company.`
        + "\n\nYour clients are shared across companies and will be kept."
      : "\n\nYour clients are shared across companies and will be kept.";
    if (!window.confirm(`Delete company "${c.name}"?${warn}`)) return;
    setBusyId(c.id);
    try {
      const { data } = await api.delete(`/companies/${c.id}?cascade=true`);
      await refresh();
      toast.success(
        `Company deleted${data?.docs_deleted ? ` along with ${data.docs_deleted} document(s)` : ""}`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const makeDefault = async (c) => {
    try {
      await switchCompany(c.id);
    } catch {
      /* surfaced by the provider */
    }
  };

  return (
    <>
      <PageHeader
        overline="Account"
        title="Companies"
        description="Every company profile on this account. The active company scopes documents, numbering and totals — clients are shared across all of them."
        testid="page-companies"
        actions={
          <>
            <span
              className="text-sm px-4 h-10 border border-[var(--border)] rounded-md leading-10 inline-flex items-center num"
              data-testid="company-limit-indicator"
            >
              {used} of {limit} used
            </span>
            <button
              type="button"
              onClick={() => setEditing("new")}
              disabled={!canAddCompany}
              title={
                canAddCompany
                  ? "Add a company profile"
                  : `Limit reached. Ask an administrator to raise your limit above ${limit}.`
              }
              className="btn-primary text-sm h-10 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="btn-add-company"
            >
              <Plus className="w-4 h-4" /> Add company
            </button>
          </>
        }
      />

      <Content>
        {!canAddCompany && used > 0 && (
          <div
            className="card-flat p-4 mb-5 flex items-start gap-3 text-sm"
            data-testid="company-limit-notice"
          >
            <EyeOff className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">
                You have reached the limit of {limit} company profiles.
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                An administrator can raise your limit from the admin panel if you need more.
              </div>
            </div>
          </div>
        )}

        {editing === "new" && (
          <div className="card-flat p-5 mb-5" data-testid="company-create-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">New company</div>
                <div className="text-lg font-medium mt-0.5">Add a company profile</div>
              </div>
              <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => setEditing(null)}
                      aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <CompanyForm
              initial={null}
              onSaved={createCompany}
              onCancel={() => setEditing(null)}
              submitLabel="Add company"
            />
          </div>
        )}

        {companies.length === 0 && editing !== "new" ? (
          <div className="card-flat p-10 text-center">
            <Building2 className="w-8 h-8 text-gray-300 mx-auto" />
            <div className="text-base font-medium mt-3">No companies yet</div>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              Add your first company profile to start issuing invoices and receipts. You can
              hold up to {limit}.
            </p>
            <button onClick={() => setEditing("new")}
                    className="btn-primary text-sm h-10 px-5 mt-4 inline-flex items-center gap-2"
                    data-testid="btn-add-company-empty">
              <Plus className="w-4 h-4" /> Add company
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="company-list">
            {companies.map((c) => {
              const isActive = c.id === activeCompany?.id;
              return (
                <div
                  key={c.id}
                  className={`card-flat p-4 ${isActive ? "ring-1 ring-[var(--primary)]" : ""}`}
                  data-testid={`company-card-${c.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-[var(--muted)] grid place-items-center shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate" data-testid={`company-name-${c.id}`}>
                          {c.name || "Unnamed company"}
                        </span>
                        {c.label && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            {c.label}
                          </span>
                        )}
                        {isActive && (
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--primary)] text-white"
                            data-testid={`company-active-badge-${c.id}`}
                          >
                            Active
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{c.gstin || "No GSTIN"}</span>
                        <span>{c.base_currency}</span>
                        <span className="num">{c.doc_count || 0} documents</span>
                        <span className="num">{c.client_count || 0} clients billed</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!isActive && (
                        <button onClick={() => makeDefault(c)}
                                className="text-xs px-3 h-8 border border-[var(--border)] rounded-md hover:bg-gray-50"
                                data-testid={`company-activate-${c.id}`}>
                          Make active
                        </button>
                      )}
                      <button onClick={() => setEditing(c.id)}
                              className="p-2 hover:bg-gray-100 rounded"
                              title="Edit company" data-testid={`company-edit-${c.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeCompany(c)} disabled={busyId === c.id}
                              className="p-2 hover:bg-gray-100 rounded text-red-600 disabled:opacity-50"
                              title="Delete company" data-testid={`company-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editingCompany && (
          <div className="card-flat p-5 mt-5" data-testid="company-edit-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Edit company</div>
                <div className="text-lg font-medium mt-0.5">
                  {editingCompany.name || "Unnamed company"}
                </div>
              </div>
              <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => setEditing(null)}
                      aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <CompanyForm
              key={editingCompany.id}
              initial={editingCompany}
              onSaved={saveCompany}
              onCancel={() => setEditing(null)}
            />
          </div>
        )}

        <p className="text-xs text-gray-500 mt-6">
          Need more than {limit} profiles? An administrator can raise your company limit
          from the admin panel.
        </p>
      </Content>
    </>
  );
}